import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { colors, ThemeColors } from '../styles/colors';

interface ThemeContextType {
  colors: ThemeColors;
  isDark: boolean;
  setDarkMode: (isDark: boolean) => void;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(false);
  
  useEffect(() => {
    // Load theme preference from localStorage
    const storedTheme = localStorage.getItem('darkMode');
    if (storedTheme !== null) {
      setDarkMode(storedTheme === 'true');
    }
  }, []);
  
  const handleSetDarkMode = (isDark: boolean) => {
    setDarkMode(isDark);
    localStorage.setItem('darkMode', String(isDark));
  };
  
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    handleSetDarkMode(newMode);
  };
  
  const theme = {
    colors: darkMode ? colors.dark : colors.light,
    isDark: darkMode,
    setDarkMode: handleSetDarkMode,
    toggleDarkMode,
  };
  
  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Add this line to export the context itself
export default ThemeContext;